import { useRef } from 'react';
import { API } from '../../utils/amplifyWrapper';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { assertFeatureCreditsOk } from '../organisation/featureCreditsGuard';

// Fetch all cloned voices
export const fetchClonedVoices = async (org_id: string, integration_id: string) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/cloned_voice`,
        {
            headers: { 'Content-Type': 'application/json' }
        }
    );
    return response.data;
};

// Fetch a single cloned voice by ID
export const fetchClonedVoiceById = async (org_id: string, integration_id: string, voice_id: string) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/cloned_voice/${voice_id}`,
        {
            headers: { 'Content-Type': 'application/json' }
        }
    );
    return response.data;
};

interface IUseClonedVoicesProps {
    org_id: string;
    integration_id: string;
    pollWhileProcessing?: boolean;
    enabled?: boolean;
}

export const useClonedVoices = ({ org_id, integration_id, pollWhileProcessing = false, enabled = true }: IUseClonedVoicesProps) => {
    const queryClient = useQueryClient();
    // Covers clone, design and mix (all stored in the cloned_voice table). Detect a job settling
    // to refresh the credit balance — these are billed on completion (flat per job).
    const prevActiveRef = useRef<number | null>(null);
    return useQuery(
        ['cloned_voices', org_id, integration_id],
        () => fetchClonedVoices(org_id, integration_id),
        {
            enabled: !!(org_id && integration_id) && enabled,
            refetchInterval: pollWhileProcessing
                ? (data: any) => (Array.isArray(data) && data.some((voice: any) => voice.status === 'processing') ? 3000 : false)
                : false,
            onSuccess: (data: any) => {
                const active = Array.isArray(data) ? data.filter((voice: any) => voice.status === 'processing').length : 0;
                if (prevActiveRef.current !== null && active < prevActiveRef.current) {
                    queryClient.invalidateQueries(['organisation_usage', org_id]);
                }
                prevActiveRef.current = active;
            },
        }
    );
};

// Get presigned URL for uploading audio file
interface IGetPresignedUrlParams {
    org_id: string;
    integration_id: string;
    fileName: string;
    contentType: string;
}

export const fetchPresignedUrl = async ({
    org_id,
    integration_id,
    fileName,
    contentType
}: IGetPresignedUrlParams) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/audio/signed-url?file_name=${encodeURIComponent(fileName)}&content_type=${encodeURIComponent(contentType)}&file_name_prefix=voice-clone&file_type=cloned-voice`,
        {
            headers: { 'Content-Type': 'application/json' }
        }
    );
    return response.data;
};

export const useGetPresignedUrl = () => {
    return useMutation(
        async (params: IGetPresignedUrlParams) => {
            return await fetchPresignedUrl(params);
        }
    );
};

// Upload file to S3 using presigned URL (not a React Query hook since it's just a fetch call)
export const uploadToS3 = async (presignedUrl: string, file: File): Promise<void> => {
    const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': file.type
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.statusText}`);
    }
};

// Create cloned voice record
interface ICreateClonedVoiceParams {
    org_id: string;
    integration_id: string;
    voice_name: string;
    language_code: string;
    description: string;
    audio_sample_path: string;
    audio_duration: number;
}

export const useCreateClonedVoice = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({
            org_id,
            integration_id,
            voice_name,
            language_code,
            description,
            audio_sample_path,
            audio_duration
        }: ICreateClonedVoiceParams) => {
            const response = await API.post(
                'main',
                `/organisation/${org_id}/integration/${integration_id}/cloned_voice`,
                {
                    body: {
                        voice_name,
                        language_code,
                        description,
                        audio_sample_path,
                        audio_duration
                    },
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            assertFeatureCreditsOk(response);
            return { voiceId: response.data.id as string, org_id, integration_id };
        },
        {
            onSuccess: ({ org_id, integration_id }) => {
                queryClient.invalidateQueries(['cloned_voices', org_id, integration_id]);
            }
        }
    );
};

// Update cloned voice
interface IUpdateClonedVoiceParams {
    org_id: string;
    integration_id: string;
    voice_id: string;
    voice_name?: string;
    description?: string;
    status?: 'processing' | 'ready' | 'failed';
}

export const useUpdateClonedVoice = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({
            org_id,
            integration_id,
            voice_id,
            voice_name,
            description,
            status
        }: IUpdateClonedVoiceParams) => {
            const response = await API.put(
                'main',
                `/organisation/${org_id}/integration/${integration_id}/cloned_voice/${voice_id}`,
                {
                    body: {
                        voice_name,
                        description,
                        status
                    },
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            return { response, org_id, integration_id };
        },
        {
            onSuccess: ({ org_id, integration_id }) => {
                queryClient.invalidateQueries(['cloned_voices', org_id, integration_id]);
            }
        }
    );
};

// Delete cloned voice
interface IDeleteClonedVoiceParams {
    org_id: string;
    integration_id: string;
    voice_id: string;
}

export const useDeleteClonedVoice = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ org_id, integration_id, voice_id }: IDeleteClonedVoiceParams) => {
            const response = await API.del(
                'main',
                `/organisation/${org_id}/integration/${integration_id}/cloned_voice/${voice_id}`,
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            return { response, org_id, integration_id };
        },
        {
            onSuccess: ({ org_id, integration_id }) => {
                queryClient.invalidateQueries(['cloned_voices', org_id, integration_id]);
            }
        }
    );
};

// Generate preview with cloned voice
interface IClonedVoicePreviewParams {
    org_id: string;
    integration_id: string;
    audio_sample_url: string;
    language_code: string;
}

export const useClonedVoicePreview = () => {
    return useMutation(
        async ({
            org_id,
            integration_id,
            audio_sample_url,
            language_code
        }: IClonedVoicePreviewParams) => {
            const response = await API.post(
                'main',
                `/organisation/${org_id}/integration/${integration_id}/cloned_voice/preview`,
                {
                    body: {
                        audio_sample_url,
                        language_code
                    },
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            return response.data;
        }
    );
};
